import { exchangeRate, todayInTimezone, toDecimal } from '@mm/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { ChevronLeft, ChevronRight, Save, Undo2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { api, ApiError } from '../../api/client';
import { Button } from '../../components/ui/Button';
import { Alert, Card, PageHeader, Spinner } from '../../components/ui/display';
import { useToast } from '../../components/ui/Toast';
import { useLookups } from '../../hooks/useLookups';
import type { CurrencyRate } from '../../types/models';
import { formatDecimal, monthName } from '../../utils/format';
import { useAuth } from '../auth/AuthProvider';
import { resourceKey } from '../masters/MasterPage';
import { RecalculateCard } from './RecalculateCard';

const MONTHS = Array.from({ length: 12 }, (_, i) => i + 1);
const cellKey = (code: string, month: number) => `${code}-${month}`;

function sameRate(a: string, b: string | undefined): boolean {
  if (!b) return a.trim() === '';
  try {
    return toDecimal(a).equals(toDecimal(b));
  } catch {
    return false;
  }
}

/**
 * Monthly exchange-rate grid. Saved rates apply to revenue entered later; each
 * revenue entry freezes the rate it used, so changes here never alter history.
 */
export function CurrencyRatesPage() {
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { data: lookups } = useLookups();
  const today = todayInTimezone();
  const [year, setYear] = useState(Number(today.slice(0, 4)));
  const [draft, setDraft] = useState<Record<string, string>>({});
  const editable = can('currencyRates.write');

  const query = useQuery({
    queryKey: [...resourceKey('currency-rates'), 'year', year],
    queryFn: () => api.list<CurrencyRate>('/currency-rates', { year, pageSize: 500 }),
  });

  const saved = useMemo(() => {
    const map: Record<string, string> = {};
    for (const r of query.data?.data ?? [])
      map[cellKey(r.currencyCode, r.month)] = r.exchangeRateToInr;
    return map;
  }, [query.data]);

  useEffect(() => {
    setDraft(Object.fromEntries(Object.entries(saved).map(([k, v]) => [k, formatDecimal(v)])));
  }, [saved]);

  const currencies = useMemo(() => {
    const codes = new Set(
      (lookups?.currencies ?? [])
        .filter((c) => c.status === 'ACTIVE' && c.code !== 'INR')
        .map((c) => c.code),
    );
    for (const r of query.data?.data ?? []) codes.add(r.currencyCode);
    return [...codes].sort();
  }, [lookups, query.data]);

  const changes = useMemo(
    () =>
      currencies.flatMap((code) =>
        MONTHS.flatMap((month) => {
          const k = cellKey(code, month);
          const value = draft[k] ?? '';
          if (sameRate(value, saved[k])) return [];
          return [{ key: k, code, month, value }];
        }),
      ),
    [currencies, draft, saved],
  );
  const invalid = new Set(
    changes.filter((c) => !exchangeRate.safeParse(c.value).success).map((c) => c.key),
  );

  const save = useMutation({
    mutationFn: () =>
      api.post<{ created: number; updated: number }>('/currency-rates/bulk', {
        rates: changes.map((c) => ({
          currencyCode: c.code,
          year,
          month: c.month,
          exchangeRateToInr: c.value,
        })),
      }),
    onSuccess: (r) => {
      toast.success(`Rates saved (${r.created} added, ${r.updated} updated)`);
      void queryClient.invalidateQueries({ queryKey: resourceKey('currency-rates') });
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Saving rates failed'),
  });

  const currentMonth = Number(today.slice(5, 7));
  const isCurrentYear = Number(today.slice(0, 4)) === year;

  return (
    <>
      <PageHeader
        title="Currency rates"
        description="Monthly rates to INR (1 unit of currency = ₹ rate). Revenue entries use the rate of their transaction month."
        actions={
          editable && (
            <>
              <Button
                variant="secondary"
                icon={<Undo2 className="size-4" />}
                disabled={changes.length === 0}
                onClick={() =>
                  setDraft(
                    Object.fromEntries(
                      Object.entries(saved).map(([k, v]) => [k, formatDecimal(v)]),
                    ),
                  )
                }
              >
                Discard
              </Button>
              <Button
                icon={<Save className="size-4" />}
                disabled={changes.length === 0 || invalid.size > 0}
                loading={save.isPending}
                onClick={() => save.mutate()}
              >
                Save{' '}
                {changes.length > 0
                  ? `${changes.length} change${changes.length === 1 ? '' : 's'}`
                  : 'changes'}
              </Button>
            </>
          )
        }
      />

      <div className="mb-4">
        <Alert tone="info">
          Changing a rate here does <strong>not</strong> change revenue already saved — each entry
          keeps the rate it was saved with. An administrator can explicitly recalculate past entries
          below.
        </Alert>
      </div>

      <Card
        bodyClassName="p-0"
        title={
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="rounded-md p-1 hover:bg-slate-100"
              onClick={() => setYear((y) => y - 1)}
              aria-label="Previous year"
            >
              <ChevronLeft className="size-4" />
            </button>
            <span className="num w-12 text-center">{year}</span>
            <button
              type="button"
              className="rounded-md p-1 hover:bg-slate-100"
              onClick={() => setYear((y) => y + 1)}
              aria-label="Next year"
            >
              <ChevronRight className="size-4" />
            </button>
          </div>
        }
      >
        {query.isLoading ? (
          <Spinner />
        ) : currencies.length === 0 ? (
          <p className="p-6 text-sm text-slate-500">
            No foreign currencies are active. Add one under Settings → Currencies.
          </p>
        ) : (
          <div className="relative overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold tracking-wide text-slate-500 uppercase">
                    Month
                  </th>
                  {currencies.map((code) => (
                    <th
                      key={code}
                      className="px-4 py-3 text-right text-xs font-semibold tracking-wide text-slate-500 uppercase"
                    >
                      {code} → INR
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {MONTHS.map((month) => (
                  <tr
                    key={month}
                    className={clsx(isCurrentYear && month === currentMonth && 'bg-brand-50/40')}
                  >
                    <td className="px-4 py-2 font-medium whitespace-nowrap text-slate-700">
                      {monthName(month, true)}
                      {isCurrentYear && month === currentMonth && (
                        <span className="ml-2 text-xs font-normal text-brand-600">current</span>
                      )}
                    </td>
                    {currencies.map((code) => {
                      const k = cellKey(code, month);
                      const changed = changes.some((c) => c.key === k);
                      const bad = invalid.has(k);
                      return (
                        <td key={code} className="px-4 py-1.5 text-right">
                          {editable ? (
                            <input
                              inputMode="decimal"
                              aria-label={`${code} rate for ${monthName(month, true)} ${year}`}
                              value={draft[k] ?? ''}
                              placeholder="Not set"
                              onChange={(e) =>
                                setDraft((d) => ({ ...d, [k]: e.target.value.trim() }))
                              }
                              className={clsx(
                                'num h-9 w-32 rounded-md border px-2 text-right text-sm focus:ring-2 focus:outline-none',
                                bad
                                  ? 'border-rose-400 focus:ring-rose-200'
                                  : changed
                                    ? 'border-amber-400 bg-amber-50 focus:ring-amber-200'
                                    : 'border-slate-200 focus:border-brand-500 focus:ring-brand-500/20',
                                !draft[k] && 'placeholder:text-amber-500',
                              )}
                            />
                          ) : (
                            <span className={clsx('num', !saved[k] && 'text-amber-600')}>
                              {saved[k] ? `₹${formatDecimal(saved[k])}` : 'Not set'}
                            </span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {invalid.size > 0 && (
          <p className="border-t border-slate-100 px-4 py-3 text-sm text-rose-600">
            Rates must be greater than zero with at most 6 decimal places. Clearing a saved rate is
            not supported — enter the correct value instead.
          </p>
        )}
      </Card>
      {can('fx.recalculate') && currencies.length > 0 && (
        <RecalculateCard key={year} year={year} currencies={currencies} />
      )}
    </>
  );
}
